import { useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Upload } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button, Input, cn, toast } from '@cyphex/ui';
import { EMOJI_MAX_BYTES, EMOJI_MIME_TYPES, readImageAsBase64 } from '../api/emojiApi';
import { useCreateEmoji } from '../hooks/useEmojiMutations';

/** Mirrors the server rule in apps/api/src/services/emoji.ts exactly. */
const uploadSchema = z.object({
  name: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      /^[a-z0-9][a-z0-9_+-]{0,46}[a-z0-9]$/,
      'Use 2-48 lowercase letters, numbers, dash, plus or underscore.',
    ),
});

type UploadValues = z.infer<typeof uploadSchema>;

export interface EmojiUploadFormProps {
  disabled?: boolean;
  disabledReason?: string;
}

const KB = 1024;

/**
 * Upload one emoji.
 *
 * The file is validated in the browser BEFORE it is encoded: base64 inflates a
 * 256 KB image to ~350 KB, and rejecting a 4 MB PNG after uploading it is the
 * worst of both worlds. The server re-checks everything regardless — this is a
 * courtesy, not the control.
 */
export function EmojiUploadForm({ disabled = false, disabledReason }: EmojiUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const form = useForm<UploadValues>({
    resolver: zodResolver(uploadSchema),
    defaultValues: { name: '' },
  });

  const { create, isCreating } = useCreateEmoji(() => {
    form.reset({ name: '' });
    setFile(null);
    setPreview(null);
    if (inputRef.current !== null) inputRef.current.value = '';
  });

  const pick = (chosen: File | undefined): void => {
    if (chosen === undefined) return;
    if (!EMOJI_MIME_TYPES.includes(chosen.type)) {
      toast.error('Emoji must be a PNG, GIF, WebP or JPEG image.');
      return;
    }
    if (chosen.size > EMOJI_MAX_BYTES) {
      toast.error(`Emoji images must be ${EMOJI_MAX_BYTES / KB} KB or smaller.`);
      return;
    }
    setFile(chosen);
    setPreview(URL.createObjectURL(chosen));
    // Seed the name from the filename — almost always what the user wanted.
    if (form.getValues('name') === '') {
      const stem = chosen.name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9_+-]/g, '-');
      form.setValue('name', stem.slice(0, 48));
    }
  };

  const submit = form.handleSubmit(async (values) => {
    if (file === null) {
      toast.error('Choose an image first.');
      return;
    }
    const data = await readImageAsBase64(file).catch(() => null);
    if (data === null) {
      toast.error('That file could not be read.');
      return;
    }
    create({ name: values.name, data, contentType: file.type });
  });

  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-dashed border-border-strong bg-subtle">
        {preview === null ? (
          <Upload size={16} className="text-muted-foreground" aria-hidden />
        ) : (
          <img src={preview} alt="Selected emoji preview" className="h-8 w-8 object-contain" />
        )}
      </div>

      <div className="flex-1 basis-40">
        <label
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5',
            'text-md font-medium text-secondary-foreground hover:bg-subtle',
            disabled && 'pointer-events-none opacity-60',
          )}
        >
          <Upload size={14} aria-hidden />
          {file === null ? 'Choose image' : file.name}
          <input
            ref={inputRef}
            type="file"
            accept={EMOJI_MIME_TYPES.join(',')}
            className="sr-only"
            disabled={disabled}
            onChange={(event) => pick(event.target.files?.[0])}
          />
        </label>
        <p className="mt-1 text-2xs text-muted-foreground">
          PNG, GIF, WebP or JPEG, up to {EMOJI_MAX_BYTES / KB} KB.
        </p>
      </div>

      <div className="flex-1 basis-48">
        <Input
          label="Name"
          placeholder="shipit"
          disabled={disabled}
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
      </div>

      <Button type="submit" variant="primary" size="md" loading={isCreating} disabled={disabled}>
        Add emoji
      </Button>

      {disabled && disabledReason !== undefined ? (
        <p className="basis-full text-2xs text-muted-foreground">{disabledReason}</p>
      ) : null}
    </form>
  );
}
